import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Search, MapPin, Phone, Mail, Calendar, Award, Stethoscope, Building2, MessageCircle, Facebook, Instagram, ChevronRight, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function HomePage() {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    filterDoctors();
  }, [searchQuery, selectedSpecialty, doctors]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors/search`);
      setDoctors(response.data);
      setFilteredDoctors(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setLoading(false);
    }
  };

  const filterDoctors = () => {
    let filtered = doctors;

    if (searchQuery) {
      filtered = filtered.filter(
        (doc) =>
          doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (doc.workplaces || []).some(w => w.type.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedSpecialty !== "all") {
      filtered = filtered.filter((doc) => doc.specialty === selectedSpecialty);
    }

    setFilteredDoctors(filtered);
  };

  const specialties = [...new Set(doctors.map((d) => d.specialty))].filter(Boolean);

  const handleViewProfile = async (doctorId) => {
    try {
      await axios.post(`${API}/doctors/${doctorId}/view`);
      // Refresh doctors to get updated view count
      fetchDoctors();
    } catch (error) {
      console.error("Error incrementing view:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-teal-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-8 h-8 text-teal-600" />
            <h1 className="text-2xl font-bold text-gray-800">DocPlan</h1>
          </div>
          <Button
            data-testid="doctor-login-btn"
            onClick={() => navigate("/doctor/auth")}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 rounded-full"
          >
            Autentificare Doctor
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Găsește Doctorul Tău de Încredere
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Caută după nume, specialitate sau locație pentru a găsi doctori în apropiere
          </p>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 max-w-3xl mx-auto mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Caută doctori, specialități sau locații..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-teal-500 rounded-full"
              />
            </div>
            <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
              <SelectTrigger data-testid="specialty-filter" className="w-full sm:w-48 h-12 rounded-full border-gray-300">
                <SelectValue placeholder="Toate Specialitățile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate Specialitățile</SelectItem>
                {specialties.map((specialty) => (
                  <SelectItem key={specialty} value={specialty}>
                    {specialty}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Toggle */}
          <div className="flex justify-center gap-2">
            <Button
              data-testid="list-view-btn"
              onClick={() => setViewMode("list")}
              variant={viewMode === "list" ? "default" : "outline"}
              className={`rounded-full ${
                viewMode === "list"
                  ? "bg-teal-600 hover:bg-teal-700 text-white"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              Vizualizare Listă
            </Button>
            <Button
              data-testid="map-view-btn"
              onClick={() => setViewMode("map")}
              variant={viewMode === "map" ? "default" : "outline"}
              className={`rounded-full ${
                viewMode === "map"
                  ? "bg-teal-600 hover:bg-teal-700 text-white"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              Vizualizare Hartă
            </Button>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-teal-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Se încarcă doctorii...</p>
            </div>
          ) : viewMode === "list" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="doctors-list">
              {filteredDoctors.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-600 text-lg">Nu am găsit doctori. Încearcă să ajustezi căutarea.</p>
                </div>
              ) : (
                filteredDoctors.map((doctor) => (
                  <Card
                    key={doctor.id}
                    data-testid={`doctor-card-${doctor.id}`}
                    className="p-6 hover:shadow-xl transition-all duration-300 border border-gray-200 bg-white/80 backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xl font-bold">
                        {doctor.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900">{doctor.name}</h3>
                        <p className="text-sm text-teal-600 font-medium">{doctor.professional_title}</p>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Award className="w-4 h-4 text-teal-600" />
                          <span className="text-sm font-medium">{doctor.specialty}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Eye className="w-4 h-4" />
                          <span className="text-xs">{doctor.views || 0}</span>
                        </div>
                      </div>
                    </div>

                    {doctor.competencies && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{doctor.competencies}</p>
                    )}

                    {/* Workplaces - Preview (max 2) */}
                    {(doctor.workplaces || []).length > 0 && (
                      <div className="border-t border-gray-200 pt-4 mb-4 space-y-2">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Locuri de Muncă:</p>
                        {doctor.workplaces.slice(0, 2).map((workplace, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded">
                            <div className="flex items-center gap-2 text-gray-700 mb-1">
                              <Building2 className="w-4 h-4 text-teal-600" />
                              <span className="text-sm font-medium">{workplace.type}</span>
                            </div>
                            {workplace.name && (
                              <p className="text-sm text-gray-800 font-semibold ml-6 mb-1">{workplace.name}</p>
                            )}
                            {workplace.phone && (
                              <div className="flex items-center gap-2 ml-6 mb-1">
                                <Phone className="w-3 h-3 text-teal-600" />
                                <a href={`tel:${workplace.phone}`} className="text-sm text-blue-600 hover:underline">
                                  {workplace.phone}
                                </a>
                              </div>
                            )}
                            {workplace.schedule && (
                              <div className="flex items-center gap-2 ml-6 text-gray-600">
                                <Calendar className="w-3 h-3 text-teal-600" />
                                <span className="text-xs">{workplace.schedule}</span>
                              </div>
                            )}
                          </div>
                        ))}
                        {doctor.workplaces.length > 2 && (
                          <p className="text-xs text-gray-500 ml-6">+ {doctor.workplaces.length - 2} locuri de muncă</p>
                        )}
                      </div>
                    )}

                    {/* View Details Button */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                          data-testid={`view-details-${doctor.id}`}
                          onClick={() => handleViewProfile(doctor.id)}
                        >
                          Vezi Detalii Complete
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-2xl">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xl font-bold">
                                {doctor.name.charAt(0)}
                              </div>
                              <div>
                                <h2 className="text-2xl font-bold">{doctor.name}</h2>
                                <p className="text-sm text-teal-600 font-medium">{doctor.professional_title}</p>
                              </div>
                            </div>
                          </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-6 mt-4">
                          {/* Specialty */}
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">Specialitate</h3>
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-teal-600" />
                              <span>{doctor.specialty}</span>
                            </div>
                          </div>

                          {/* Competencies */}
                          {doctor.competencies && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">Competențe</h3>
                              <p className="text-gray-700">{doctor.competencies}</p>
                            </div>
                          )}

                          {/* All Workplaces */}
                          {(doctor.workplaces || []).length > 0 && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-3">Locuri de Muncă</h3>
                              <div className="space-y-3">
                                {doctor.workplaces.map((workplace, idx) => (
                                  <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-2 text-gray-900 mb-2">
                                      <Building2 className="w-5 h-5 text-teal-600" />
                                      <span className="font-semibold">{workplace.type}</span>
                                    </div>
                                    {workplace.name && (
                                      <p className="text-gray-800 font-medium mb-2 ml-7">{workplace.name}</p>
                                    )}
                                    {workplace.phone && (
                                      <div className="flex items-center gap-2 ml-7 mb-2">
                                        <Phone className="w-4 h-4 text-teal-600" />
                                        <a href={`tel:${workplace.phone}`} className="text-blue-600 hover:underline">
                                          {workplace.phone}
                                        </a>
                                      </div>
                                    )}
                                    {workplace.schedule && (
                                      <div className="flex items-start gap-2 ml-7 text-gray-600">
                                        <Calendar className="w-4 h-4 text-teal-600 mt-0.5" />
                                        <span className="text-sm">{workplace.schedule}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Contact */}
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Contact</h3>
                            <div className="space-y-2">
                              {doctor.whatsapp && (
                                <div className="flex items-center gap-2">
                                  <MessageCircle className="w-4 h-4 text-green-600" />
                                  <a
                                    href={`https://wa.me/${doctor.whatsapp.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:underline"
                                  >
                                    WhatsApp: {doctor.whatsapp}
                                  </a>
                                </div>
                              )}
                              {doctor.facebook && (
                                <div className="flex items-center gap-2">
                                  <Facebook className="w-4 h-4 text-blue-600" />
                                  <a
                                    href={doctor.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    Facebook
                                  </a>
                                </div>
                              )}
                              {doctor.instagram && (
                                <div className="flex items-center gap-2">
                                  <Instagram className="w-4 h-4 text-pink-600" />
                                  <a
                                    href={doctor.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-pink-600 hover:underline"
                                  >
                                    Instagram
                                  </a>
                                </div>
                              )}
                              {doctor.other_contacts && (
                                <div className="mt-3">
                                  <p className="text-sm text-gray-600">{doctor.other_contacts}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </Card>
                ))
              )}
            </div>
          ) : (
            <div className="h-[600px] rounded-lg overflow-hidden border border-gray-300" data-testid="map-view">
              {filteredDoctors.some(d => d.latitude && d.longitude) ? (
                <MapContainer
                  center={[45.9432, 24.9668]}
                  zoom={7}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {filteredDoctors
                    .filter((d) => d.latitude && d.longitude)
                    .map((doctor) => (
                      <Marker key={doctor.id} position={[doctor.latitude, doctor.longitude]}>
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-bold text-lg">{doctor.name}</h3>
                            <p className="text-sm text-gray-600">{doctor.specialty}</p>
                            {(doctor.workplaces || []).map((w, i) => (
                              <div key={i} className="text-sm mt-2 border-t pt-2">
                                <p className="font-medium text-teal-700">{w.type}</p>
                                {w.name && <p className="font-semibold">{w.name}</p>}
                                {w.phone && <a href={`tel:${w.phone}`} className="text-blue-600 block">{w.phone}</a>}
                                {w.schedule && <p className="text-xs text-gray-600 mt-1">{w.schedule}</p>}
                              </div>
                            ))}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-100">
                  <p className="text-gray-600">Nu există doctori cu date de locație disponibile</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">&copy; 2025 DocPlan. Găsește furnizorul tău de încredere de sănătate.</p>
        </div>
      </footer>
    </div>
  );
}
